import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import { IconButton, Chip, Box, Typography, Button, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { addDays, differenceInMonths } from 'date-fns';
import { Edit, MoreVertical, Copy, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ContractEntity } from 'src/entities/contractEntity';
import { dataGridUtil } from 'src/utils/dataGrid.util';

const ActionCell = ({
  row,
  openEditDialog,
  handleDeleteSingleContract,
  handleCopyContract
}: {
  row: ContractEntity;
  openEditDialog?: (entity: ContractEntity) => void;
  handleDeleteSingleContract?: (contractId: number, contractorName: string) => void;
  handleCopyContract?: (entity: ContractEntity) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    openEditDialog?.(row);
    handleMenuClose();
  };

  const handleCopy = () => {
    handleCopyContract?.(row);
    handleMenuClose();
  };

  const handleDelete = () => {
    handleDeleteSingleContract?.(row.id, row.contractor_name);
    handleMenuClose();
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          size="small"
          startIcon={<Edit size={16} />}
          onClick={() => openEditDialog?.(row)}
        >
          編集
        </Button>
        <IconButton
          size="small"
          onClick={handleMenuClick}
        >
          <MoreVertical size={16} />
        </IconButton>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              p: 0,
              m: 0,
            }
          }
        }}
      >
        <MenuItem onClick={handleEdit} sx={{ py: 0.5, px: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Edit size={16} />
          </ListItemIcon>
          <ListItemText primary="編集" />
        </MenuItem>
        <MenuItem onClick={handleCopy} sx={{ py: 0.5, px: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Copy size={16} />
          </ListItemIcon>
          <ListItemText primary="複製" />
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ py: 0.5, px: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 32, color: 'error.main' }}>
            <Trash2 size={16} />
          </ListItemIcon>
          <ListItemText primary="削除" primaryTypographyProps={{ sx: { color: 'error.main' } }} />
        </MenuItem>
      </Menu>
    </>
  );
};

export const Columns: (
  openEditDialog?: (entity: ContractEntity) => void,
  openDocumentPreviewDialog?: (entity: ContractEntity) => void,
  handleDeleteSingleContract?: (contractId: number, contractorName: string) => void,
  handleCopyContract?: (entity: ContractEntity) => void,
  openPricingDetailsDialog?: (entity: ContractEntity) => void
) => GridColDef[] = (
  openEditDialog,
  openDocumentPreviewDialog,
  handleDeleteSingleContract,
  handleCopyContract,
  openPricingDetailsDialog
) => {
  const getWidth = (name: string, width: number) => {
    const maybeWidth: number = dataGridUtil.getSavedColumnWidth('contract-columns', name);

    return maybeWidth || width;
  };

  const getPricingMethodColor = (method: string) => {
    switch (method) {
      case '個建て':
        return 'primary';
      case '車建て':
        return 'default';
      case 'トンキロ建て':
        return 'info';
      default:
        return 'default';
    }
  };

  return [
    {
      field: 'company_name',
      headerName: '事業所',
      width: getWidth('company_name', 200),
    },
    {
      field: 'contractor_name',
      headerName: '契約先',
      width: getWidth('contractor_name', 250),
      renderCell: (params: { row: ContractEntity }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
            {params.row.contractor_name || '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            ID: {params.row.id?.toString().padStart(10, '0') || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'contract_period',
      headerName: '契約期間',
      width: getWidth('contract_period', 200),
      sortable: false,
      renderCell: (params: { row: ContractEntity }) => {
        const { start_date, end_date } = params.row;
        let durationText = '';

        if (start_date && end_date) {
          try {
            const startDate = new Date(start_date);
            const endDate = new Date(end_date);
            // 終了日+1日で月数を計算（4/1〜4/30 = 1ヶ月, 4/1〜翌3/31 = 12ヶ月）
            const endDatePlusOne = addDays(endDate, 1);
            const months = differenceInMonths(endDatePlusOne, startDate);
            durationText = `約${months}ヶ月間`;
          } catch (_e) {
            durationText = '';
          }
        }

        return (
          <Box>
            <Typography variant="body2">
              {start_date || '-'} ～ {end_date || '-'}
            </Typography>
            {durationText && (
              <Typography variant="caption" color="text.secondary">
                {durationText}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'ステータス',
      width: getWidth('status', 120),
      renderCell: (params: { row: ContractEntity }) => (
        <Chip
          icon={params.row.status === 'active' ? <CheckCircleIcon /> : <CancelIcon />}
          label={params.row.status === 'active' ? '有効' : '無効'}
          color={params.row.status === 'active' ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'pricing_method_summary',
      headerName: '課金方式',
      width: getWidth('pricing_method_summary', 150),
      sortable: false,
      renderCell: (params: { row: ContractEntity }) => {
        if (!params.row.pricing_settings || params.row.pricing_settings.length === 0) {
          return '-';
        }

        const methods = params.row.pricing_settings.map((s) => s.pricing_method);
        const uniqueMethods = [...new Set(methods)];

        if (uniqueMethods.length === 1) {
          return (
            <Chip
              label={uniqueMethods[0]}
              color={getPricingMethodColor(uniqueMethods[0])}
              size="small"
              variant="outlined"
            />
          );
        }

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {uniqueMethods.slice(0, 2).map((method, index) => (
              <Chip
                key={index}
                label={method}
                color={getPricingMethodColor(method)}
                size="small"
                variant="outlined"
              />
            ))}
            {uniqueMethods.length > 2 && (
              <Typography variant="caption" color="text.secondary">
                他{uniqueMethods.length - 2}件
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'base_amount_range',
      headerName: '基本料金',
      width: getWidth('base_amount_range', 150),
      align: 'left',
      headerAlign: 'left',
      sortable: false,
      renderCell: (params: { row: ContractEntity }) => {
        const { pricing_settings } = params.row;

        if (!pricing_settings || pricing_settings.length === 0) {
          return '-';
        }

        // 金額の取得（トンキロ建ての場合はdistance_weight_settingsから取得）
        const amounts = pricing_settings.flatMap((s) => {
          if (s.pricing_method === 'トンキロ建て' && s.distance_weight_settings) {
            return s.distance_weight_settings
              .map(dw => dw.price)
              .filter((price): price is number => price !== null && price !== undefined);
          }
          return [s.base_amount];
        });

        if (amounts.length === 0) {
          return '-';
        }

        const min = Math.min(...amounts);
        const max = Math.max(...amounts);
        const isSingle = pricing_settings.length === 1;

        // 単位の決定
        const getUnit = (method: string) => {
          switch (method) {
            case 'トンキロ建て':
              return '/トンキロ';
            case '車建て':
              return '/台';
            case '個建て':
              return '/件';
            default:
              return '';
          }
        };

        // 単一設定の場合の金額取得
        const getSingleAmount = () => {
          const setting = pricing_settings[0];
          if (setting.pricing_method === 'トンキロ建て' && setting.distance_weight_settings) {
            const prices = setting.distance_weight_settings
              .map(dw => dw.price)
              .filter((price): price is number => price !== null && price !== undefined);
            if (prices.length === 0) return null;
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            return minPrice === maxPrice ? minPrice : null;
          }
          return setting.base_amount;
        };

        const singleAmount = isSingle ? getSingleAmount() : null;

        return (
          <Box>
            {/* 金額表示 */}
            <Typography variant="body2" fontWeight="bold">
              {isSingle && singleAmount !== null
                ? `¥${singleAmount.toLocaleString()}`
                : `¥${min.toLocaleString()} ～ ¥${max.toLocaleString()}`
              }
            </Typography>

            {/* 単一設定の場合のみ単位を表示 */}
            {isSingle && (
              <Typography variant="caption" color="text.secondary">
                {getUnit(pricing_settings[0].pricing_method)}
              </Typography>
            )}

            {/* 詳細表示リンク */}
            <Typography
              variant="caption"
              color="primary"
              sx={{
                cursor: 'pointer',
                textDecoration: 'underline',
                display: 'block',
                mt: 0.5,
                '&:hover': {
                  textDecoration: 'none'
                }
              }}
              onClick={(e) => {
                e.stopPropagation(); // DataGridのrowClickイベントを防止
                openPricingDetailsDialog?.(params.row);
              }}
            >
              詳細表示
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'applicable_conditions',
      headerName: '適用条件',
      width: getWidth('applicable_conditions', 200),
      sortable: false,
      renderCell: (params: { row: ContractEntity }) => {
        if (!params.row.pricing_settings || params.row.pricing_settings.length === 0) {
          return '-';
        }

        const allItems: string[] = [];
        params.row.pricing_settings.forEach((setting) => {
          setting.area_settings?.forEach((area) => {
            allItems.push(`${area.loading_area}→${area.unloading_area}`);
          });
        });

        const uniqueItems = [...new Set(allItems)];

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {uniqueItems.slice(0, 2).map((item, index) => (
              <Chip
                key={index}
                label={item}
                size="small"
                variant="outlined"
              />
            ))}
            {uniqueItems.length > 2 && (
              <Chip
                label={`他${uniqueItems.length - 2}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        );
      },
    },
    {
      field: 'has_document',
      headerName: '契約書',
      width: getWidth('has_document', 100),
      renderCell: (params: { row: ContractEntity }) => {
        const hasDocument = params.row.has_document && params.row.documents && params.row.documents.length > 0;

        return (
          <Tooltip title={hasDocument ? '契約書を表示' : '契約書なし'}>
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasDocument) {
                    openDocumentPreviewDialog?.(params.row);
                  }
                }}
                disabled={!hasDocument}
              >
                <DescriptionIcon
                  color={hasDocument ? 'success' : 'disabled'}
                />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'payment_terms',
      headerName: '請求/支払設定',
      width: getWidth('payment_terms', 180),
      renderCell: (params: { row: ContractEntity }) => {
        let closing_day = params.row.closing_day;
        if (closing_day && closing_day !== '末日') {
          closing_day = `${closing_day}日`;
        }
        let payment_day = params.row.payment_day;
        if (payment_day && payment_day !== '末日') {
          payment_day = `${payment_day}日`;
        }

        return (
          <Box>
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              締め: {closing_day || '-'}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              {params.row.contract_type === 'billing' ? '入金' : '支払'}:
              {params.row.payment_month || '-'} {payment_day || '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'updated_at',
      headerName: '最終更新',
      width: getWidth('updated_at', 130),
      type: 'date',
      valueGetter: (_, row: ContractEntity) =>
        row.updated_at ? new Date(row.updated_at) : null,
    },
    {
      field: 'edit',
      headerName: '操作',
      disableExport: true,
      width: 150,
      renderCell: (params: { row: ContractEntity }) => (
        <ActionCell
          row={params.row}
          openEditDialog={openEditDialog}
          handleDeleteSingleContract={handleDeleteSingleContract}
          handleCopyContract={handleCopyContract}
        />
      ),
      filterable: false,
      sortable: false,
    },
  ];
};
